import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Mail, AlertCircle, Sparkles, Moon, Plane } from 'lucide-react';
import { contactInfo } from '../data/mock';
import { publicApi } from '../lib/api';

const CALENDLY_SCRIPT = 'https://assets.calendly.com/assets/external/widget.js';

const waPrefill = encodeURIComponent(
  "Hi Dawn! I'd like to book a treatment with Blissfull Mobile Spa. Could you confirm a slot?"
);

const durations = [
  {
    id: '45',
    label: '45 MIN',
    tag: 'Bliss Massage',
    icon: Sparkles,
    priceRange: 'from R680',
    services: ['Foot ritual R680', 'Back, neck & shoulder R789', 'Indian head R800'],
  },
  {
    id: '60',
    label: '60 MIN',
    tag: 'Total Time Out',
    icon: Moon,
    priceRange: 'from R850',
    services: ['Back of body R850', 'Swedish R890', 'Detox R950', 'Deep tissue R1100', 'Mom-to-be R1100'],
  },
  {
    id: '90',
    label: '90 MIN',
    tag: 'Full Escapes',
    icon: Plane,
    priceRange: 'from R1200',
    services: ['Blissful signature R1350', 'Cupping R1200', 'Deep tissue R1400', 'Sports R1480', 'Mom-to-be R1300'],
  },
];

const Availability = () => {
  const [active, setActive] = useState('60');
  const [urls, setUrls] = useState({ calendly_45: '', calendly_60: '', calendly_90: '' });

  // Load calendly script once
  useEffect(() => {
    if (document.querySelector(`script[src="${CALENDLY_SCRIPT}"]`)) return;
    const s = document.createElement('script');
    s.src = CALENDLY_SCRIPT;
    s.async = true;
    document.body.appendChild(s);
  }, []);

  // Load configured Calendly URLs from backend
  useEffect(() => {
    publicApi.get('/settings')
      .then((r) => setUrls({
        calendly_45: r.data.calendly_45 || '',
        calendly_60: r.data.calendly_60 || '',
        calendly_90: r.data.calendly_90 || '',
      }))
      .catch(() => {});
  }, []);

  const activeUrl = urls[`calendly_${active}`];

  // Force Calendly to re-init when the URL changes
  const widgetKey = useMemo(() => `${active}-${activeUrl}`, [active, activeUrl]);

  useEffect(() => {
    if (window.Calendly && activeUrl) {
      const nodes = document.querySelectorAll('.calendly-inline-widget');
      nodes.forEach((n) => { n.innerHTML = ''; });
      try { window.Calendly.initInlineWidgets(); } catch (_) {}
    }
  }, [widgetKey, activeUrl]);

  const activeDur = durations.find((d) => d.id === active);

  return (
    <section id="availability" className="relative py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="text-center mb-10">
          <p className="text-white/80 tracking-[0.3em] text-xs md:text-sm font-light mb-4" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
            AVAILABILITY
          </p>
          <h2 className="text-white font-light" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2rem, 4.5vw, 3.5rem)' }}>
            Pick a real time that suits you
          </h2>
          <p className="text-white/90 mt-4 italic font-light text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Choose your treatment length, then pick an open slot on the live calendar.
          </p>
          <div className="mt-6 mx-auto w-24 h-px bg-white/50" />
        </motion.div>

        {/* Duration tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-6">
          {durations.map((d) => {
            const Icon = d.icon;
            const isActive = d.id === active;
            return (
              <button
                key={d.id}
                onClick={() => setActive(d.id)}
                className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-xs tracking-[0.2em] font-light transition-all duration-500 border ${
                  isActive
                    ? 'bg-white text-rose-500 border-white shadow-lg'
                    : 'bg-white/10 text-white border-white/40 hover:bg-white/25'
                }`}
                style={{ fontFamily: "'Josefin Sans', sans-serif" }}
              >
                <Icon size={14} strokeWidth={1.6} />
                {d.label} &middot; {d.tag.toUpperCase()}
              </button>
            );
          })}
        </div>

        {/* Active service preview */}
        <div className="text-center mb-6">
          <p className="text-white/85 tracking-wide text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.05rem' }}>
            <span className="text-white/60 tracking-[0.25em] text-xs mr-2" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>{activeDur.priceRange.toUpperCase()}</span>
            {activeDur.services.join('  \u00b7  ')}
          </p>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.8 }}>
          <div className="bg-white/95 backdrop-blur-md rounded-3xl border border-white/40 p-2 sm:p-4 shadow-2xl overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={widgetKey}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {activeUrl ? (
                  <div
                    className="calendly-inline-widget"
                    data-url={activeUrl}
                    style={{ minWidth: '320px', height: '720px' }}
                  />
                ) : (
                  <div className="h-[420px] flex items-center justify-center text-neutral-500 text-center px-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.1rem' }}>
                    Dawn hasn&apos;t connected a booking calendar for this length yet. Please choose another length or WhatsApp Dawn below.
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Cancellation policy */}
          <div className="mt-6 bg-white/15 backdrop-blur-md rounded-2xl border border-white/25 px-5 py-4 flex items-start gap-3">
            <AlertCircle className="text-white/90 flex-shrink-0 mt-1" size={18} strokeWidth={1.6} />
            <div className="text-white/95" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <p className="tracking-[0.25em] text-xs text-white/80 uppercase mb-1" style={{ fontFamily: "'Josefin Sans', sans-serif" }}>
                Cancellation policy
              </p>
              <p className="text-base md:text-lg leading-relaxed">
                Bookings require <b>50% upfront</b> to secure your slot. This deposit is <b>non-refundable</b> for last-minute
                cancellations. To receive a refund, please cancel at least <b>one day before</b> your appointment.
              </p>
            </div>
          </div>

          {/* Fallback contact */}
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <a
              href={`https://wa.me/${contactInfo.whatsappIntl}?text=${waPrefill}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] bg-white/10 hover:bg-white hover:text-rose-500 transition-all"
              style={{ fontFamily: "'Josefin Sans', sans-serif" }}
            >
              <MessageCircle size={14} /> WHATSAPP DAWN
            </a>
            <a
              href={`mailto:${contactInfo.email}?subject=${encodeURIComponent('Booking enquiry')}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/70 text-white text-xs tracking-[0.25em] bg-white/10 hover:bg-white hover:text-rose-500 transition-all"
              style={{ fontFamily: "'Josefin Sans', sans-serif" }}
            >
              <Mail size={14} /> EMAIL DAWN
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Availability;
